/**
 * MetricRow Component
 *
 * Single metric row within a comparison column.
 * Displays label, value, winner/loser styling, and optional delta.
 *
 * Used inside ComparisonColumn to show individual metrics.
 *
 * Usage:
 * ```tsx
 * <MetricRow
 *   label="Total Profiles"
 *   value={89420}
 *   isWinner={true}
 *   delta={27110}
 *   format="number"
 * />
 * ```
 */

import { ArrowUp, ArrowDown } from 'lucide-react'
import type { ComparisonMetric } from '@/types/comparison'

interface MetricRowProps {
  /**
   * Metric label (e.g., "Total Profiles", "Avg Sentiment")
   */
  label: string

  /**
   * Metric value (number, string, or null)
   */
  value: number | string | null | undefined

  /**
   * Is this the winning value? (green highlight)
   */
  isWinner?: boolean

  /**
   * Delta value (difference from other subject)
   */
  delta?: number

  /**
   * Format type for display
   */
  format?: ComparisonMetric['format']

  /**
   * Optional className
   */
  className?: string
}

/**
 * Format a metric value based on its format type.
 */
function formatValue(
  value: number | string | null | undefined,
  format: ComparisonMetric['format']
): string {
  if (value === null || value === undefined) {
    return 'N/A'
  }

  if (typeof value === 'string') {
    return value
  }

  switch (format) {
    case 'number':
      return value.toLocaleString()

    case 'compact':
      return formatCompact(value)

    case 'percent':
      return `${value.toFixed(1)}%`

    case 'emotion':
    case 'text':
    default:
      return String(value)
  }
}

/**
 * Format number in compact notation (1.2M, 45K, etc.).
 */
function formatCompact(value: number): string {
  const absValue = Math.abs(value)

  if (absValue >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`
  }
  if (absValue >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (absValue >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`
  }
  return String(value)
}

/**
 * Format delta value for display.
 */
function formatDelta(delta: number, format: ComparisonMetric['format']): string {
  if (format === 'percent') {
    return `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`
  }

  if (Math.abs(delta) >= 1000) {
    return `${delta > 0 ? '+' : ''}${formatCompact(delta)}`
  }

  return `${delta > 0 ? '+' : ''}${delta.toLocaleString()}`
}

/**
 * Single metric row with label, value, winner styling, and delta.
 */
export function MetricRow({
  label,
  value,
  isWinner = false,
  delta,
  format = 'number',
  className = '',
}: MetricRowProps) {
  const formattedValue = formatValue(value, format)
  const hasDelta = delta !== undefined && delta !== 0 && typeof value === 'number'

  return (
    <div
      className={`
        metric-row
        flex items-center justify-between py-2.5 border-b border-v11-border/50
        last:border-b-0
        ${className}
      `}
    >
      {/* Label */}
      <span className="text-sm text-v11-text-muted font-medium flex-shrink-0 pr-3">
        {label}
      </span>

      {/* Value + Delta */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Value */}
        <span
          className={`
            text-sm font-semibold
            ${isWinner ? 'text-green-600 dark:text-green-400' : 'text-v11-text-primary'}
          `}
        >
          {formattedValue}
        </span>

        {/* Delta Badge (if exists) */}
        {hasDelta && (
          <span
            className={`
              text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1
              ${
                delta > 0
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              }
            `}
          >
            {delta > 0 ? (
              <ArrowUp className="h-3 w-3" />
            ) : (
              <ArrowDown className="h-3 w-3" />
            )}
            {formatDelta(Math.abs(delta), format)}
          </span>
        )}
      </div>
    </div>
  )
}

// Default export
export default MetricRow
